import { use } from "react";
import { usePathname } from "next/navigation";
import { Controller, Provider } from "@components/store-locator/controller";
import { StoreLocatorUi } from "@components/store-locator/StoreLocator";
import { AvailabilityComponentCheckout } from "@components/click-and-collect/AvailabilityComponentCheckout";
import { activeSiteConfig } from "@config/site/site-config";
import { useStoreAvailability } from "@features/stores/hooks/useStoreAvailability";
import { checkoutPage, shoppingBagPage, storeLocatorPage } from "@lib/constants/pagePaths";
import {
    useCheckoutParcelShopLocationResultsVB,
    useCheckoutParcelShopMapResultsVB,
} from "./useCheckoutParcelShopsVB";
import { useCheckoutStoreLocatorSearch } from "./useCheckoutStoreLocatorSearch";
import { ShippingMethodTypes } from "../../types";
import type { ComponentProps, JSX } from "react";

type Props = Omit<
    ComponentProps<typeof StoreLocatorUi>,
    "searchControl" | "showStoreBadge" | "renderStoreConfirmation" | "searchResults"
>;

export const CheckoutStoreLocator = (props: Props): JSX.Element => {
    const searchControl = useCheckoutStoreLocatorSearch(props.shippingMethodKey);
    const getStoresWithAvailability = useStoreAvailability(
        props.storesWithStock,
        props.shippingMethodKey !== ShippingMethodTypes.PARCEL_SHOP
    );
    const pathname = usePathname();
    const showsProductAvailability =
        props.selectedVariant &&
        !Object.values(checkoutPage).some((path) => pathname.includes(path)) &&
        !Object.values(shoppingBagPage).some((path) => pathname.includes(path));
    const showsStoreConfirmation =
        !showsProductAvailability &&
        !Object.values(storeLocatorPage).some((path) => pathname.includes(path));

    return (
        <Provider
            initStores={props.storesWithStock}
            getStoresWithAvailability={getStoresWithAvailability}
            storeLocatorLocation={props.location}
            shippingMethodKey={props.shippingMethodKey}
            onStoreSelected={props.onStoreSelected}
            searchControl={searchControl}
            showStoreBadge={props.shippingMethodKey !== ShippingMethodTypes.PARCEL_SHOP}
        >
            <CheckoutStoreLocatorResults
                {...props}
                renderStoreConfirmation={
                    showsStoreConfirmation
                        ? (store) => (
                              <AvailabilityComponentCheckout
                                  store={store}
                                  onClose={props.onClose}
                                  hideParcelShopAvailabilityComponent={
                                      props.shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP &&
                                      activeSiteConfig.hideAvailabilityInStore
                                  }
                              />
                          )
                        : undefined
                }
            />
        </Provider>
    );
};

const CheckoutStoreLocatorResults = (props: ComponentProps<typeof StoreLocatorUi>): JSX.Element => {
    const { clientLocation, searchResult, searchValidation } = use(Controller);
    const locationResults = useCheckoutParcelShopLocationResultsVB({
        clientLocation,
        shippingMethodKey: props.shippingMethodKey,
    });
    const mapResults = useCheckoutParcelShopMapResultsVB({
        clientLocation,
        searchResult,
        searchValidation,
        shippingMethodKey: props.shippingMethodKey,
    });

    return (
        <StoreLocatorUi {...props} searchResults={{ map: mapResults, location: locationResults }} />
    );
};
